import { z } from "zod";
import { subscriptionTierSchema } from "./clinic.ts";
import { planCardSchema } from "./plans.ts";

/**
 * Self-service subscription requests (G2 — public-launch commercial layer).
 * Manual bank transfer + admin approval (DEC-L1).
 */

export const subscriptionRequestStatusSchema = z.enum([
	"pending",
	"approved",
	"rejected",
]);
export type SubscriptionRequestStatus = z.infer<
	typeof subscriptionRequestStatusSchema
>;

/**
 * Platform subscription pricing (IQD per month). Defaults are keyed by facility
 * type; a clinic's own `monthly_price` (set by the super-admin) overrides. These
 * are the current de-facto anchors — adjust here (or per-clinic) when finalised.
 */
export const DEFAULT_MONTHLY_PRICE_IQD: Record<string, number> = {
	clinic: 50_000,
	lab: 75_000,
	pharmacy: 100_000,
	radiology: 100_000,
};
export const FALLBACK_MONTHLY_PRICE_IQD = 50_000;

/** Effective monthly price for a facility: its own `monthly_price` when set,
 *  else the facility-type default. Whole IQD. */
export function effectiveMonthlyPriceIqd(
	facilityType: string,
	clinicMonthlyPrice?: string | number | null,
): number {
	if (clinicMonthlyPrice != null && clinicMonthlyPrice !== "") {
		const n = Number(clinicMonthlyPrice);
		if (Number.isFinite(n) && n > 0) return Math.round(n);
	}
	return DEFAULT_MONTHLY_PRICE_IQD[facilityType] ?? FALLBACK_MONTHLY_PRICE_IQD;
}

/** Clinic owner submits a paid-tier request (trial is not purchasable). */
export const createSubscriptionRequestSchema = z.object({
	requested_tier: subscriptionTierSchema.refine((t) => t !== "trial", {
		message: "اختر باقة مدفوعة (لا يمكن طلب التجربة)",
	}),
	/** Months being paid for; the window extends by this much on approval. */
	months: z.number().int().min(1).max(36),
	amount: z.number().positive().max(100_000_000),
	currency: z.string().min(1).max(8).default("IQD"),
	/** Bank-transfer reference / proof note the owner enters after paying. */
	transfer_reference: z.string().max(500).nullable().optional(),
});
export type CreateSubscriptionRequestInput = z.infer<
	typeof createSubscriptionRequestSchema
>;

/** Admin approves/rejects with an optional note. */
export const reviewSubscriptionRequestSchema = z.object({
	note: z.string().max(1000).nullable().optional(),
});
export type ReviewSubscriptionRequestInput = z.infer<
	typeof reviewSubscriptionRequestSchema
>;

export const subscriptionRequestSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	/** Present on the admin queue (joined clinic name); null on the owner view. */
	clinic_name: z.string().nullable().optional(),
	requested_tier: subscriptionTierSchema,
	months: z.number().int(),
	amount: z.number(),
	currency: z.string(),
	transfer_reference: z.string().nullable(),
	status: subscriptionRequestStatusSchema,
	review_note: z.string().nullable(),
	reviewed_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	/** Price × months the request SHOULD have been for (facility price × months),
	 *  so the admin can catch a wrong-amount transfer. Null if price unknown. */
	expected_amount: z.number().nullable().optional(),
});
export type SubscriptionRequestApi = z.infer<typeof subscriptionRequestSchema>;

/** Where the clinic owner sends the bank transfer (platform-level, from env),
 *  plus the facility's plan catalog + effective monthly price so the UI can show
 *  the full price ladder and what each tier includes. */
export const platformBankDetailsSchema = z.object({
	details: z.string(),
	monthly_price_iqd: z.number().nullable().optional(),
	plans: z.array(planCardSchema).optional(),
});
export type PlatformBankDetails = z.infer<typeof platformBankDetailsSchema>;
